import {
  type AttributionCartItem,
  type AttributionClick,
  type AttributionProductType,
  attributionRemoveExpiredItems,
  isAttributionCartItem,
  isAttributionClick,
  isProductType,
  isRecord,
  parseStorage,
} from '../attribution'
import {
  browserStorageReadItem,
  browserStorageWriteItem,
} from '../browser-storage'

export const SEARCH_ATTRIBUTION_STORAGE_KEY = 'searchAttribution'

const SEARCH_ATTRIBUTION_VERSION = 1

export const searchAttributionSurfaces = [
  'search_page',
  'search_header_popular',
  'search_header',
] as const

export type SearchAttributionSurface =
  (typeof searchAttributionSurfaces)[number]

const legacySurfaceMigration = new Map<string, SearchAttributionSurface>([
  ['search-page', 'search_page'],
  ['searchbox-popular', 'search_header_popular'],
  ['searchbox', 'search_header'],
])

export type SearchAttributionClick = AttributionClick & {
  productType: AttributionProductType
  surface: SearchAttributionSurface
}

export type SearchAttributionCartItem = AttributionCartItem & {
  productType: AttributionProductType
  surface: SearchAttributionSurface
}

export type SearchAttributionStorage = {
  cartItems: SearchAttributionCartItem[]
  clicks: SearchAttributionClick[]
  version: typeof SEARCH_ATTRIBUTION_VERSION
}

const createEmptyStorage = (): SearchAttributionStorage => ({
  cartItems: [],
  clicks: [],
  version: SEARCH_ATTRIBUTION_VERSION,
})

const isSurface = (value: unknown): value is SearchAttributionSurface =>
  typeof value === 'string' &&
  (searchAttributionSurfaces as readonly string[]).includes(value)

const isClick = (value: unknown): value is SearchAttributionClick => {
  if (!isRecord(value)) {
    return false
  }

  const { productType, surface } = value

  return (
    isAttributionClick(value) &&
    isProductType(productType) &&
    isSurface(surface)
  )
}

const isCartItem = (value: unknown): value is SearchAttributionCartItem => {
  if (!isRecord(value)) {
    return false
  }

  const { productType, surface } = value

  return (
    isAttributionCartItem(value) &&
    isProductType(productType) &&
    isSurface(surface)
  )
}

const isStorage = (value: unknown): value is SearchAttributionStorage => {
  if (!isRecord(value)) {
    return false
  }

  return (
    value.version === SEARCH_ATTRIBUTION_VERSION &&
    Array.isArray(value.clicks) &&
    Array.isArray(value.cartItems) &&
    value.clicks.every(isClick) &&
    value.cartItems.every(isCartItem)
  )
}

/**
 * migration script for legacy item surfaces to the new format.
 *
 * The old format stays in localStorage 14 days and will lead to the
 * wrong UDP tracking values. This is to avoid this
 */
const migrateLegacyItemSurface = (value: unknown): unknown => {
  if (!isRecord(value) || typeof value.surface !== 'string') {
    return value
  }

  const surface = legacySurfaceMigration.get(value.surface)

  return surface ? { ...value, surface } : value
}

const migrateLegacyItemSurfaces = (value: unknown): unknown => {
  if (!Array.isArray(value)) {
    return value
  }

  const migratedItems = value.map((item) => migrateLegacyItemSurface(item))
  const wasMigrated = migratedItems.some((item, index) => item !== value[index])

  return wasMigrated ? migratedItems : value
}

/**
 * Legacy code. Will be removed with
 * https://jira.shop-apotheke.com/browse/ITRT-4240 in 2 weeks.
 */
const migrateStorage = (value: unknown): unknown => {
  if (!isRecord(value) || value.version !== SEARCH_ATTRIBUTION_VERSION) {
    return value
  }

  const cartItems = migrateLegacyItemSurfaces(value.cartItems)
  const clicks = migrateLegacyItemSurfaces(value.clicks)

  if (cartItems === value.cartItems && clicks === value.clicks) {
    return value
  }

  return {
    ...value,
    cartItems,
    clicks,
  }
}

export const searchAttributionWrite = (
  storage: SearchAttributionStorage,
): void => {
  let serializedStorage: string

  try {
    serializedStorage = JSON.stringify(storage)
  } catch {
    return
  }

  browserStorageWriteItem(
    'localStorage',
    SEARCH_ATTRIBUTION_STORAGE_KEY,
    serializedStorage,
  )
}

export const searchAttributionRead = (): SearchAttributionStorage => {
  const emptyStorage = createEmptyStorage()
  const rawStorage = browserStorageReadItem(
    'localStorage',
    SEARCH_ATTRIBUTION_STORAGE_KEY,
  )

  if (!rawStorage) {
    return emptyStorage
  }

  const parsedStorage = parseStorage(rawStorage)
  const migratedStorage = migrateStorage(parsedStorage)

  if (!isStorage(migratedStorage)) {
    searchAttributionWrite(emptyStorage)

    return emptyStorage
  }

  const storage = {
    ...migratedStorage,
    cartItems: attributionRemoveExpiredItems(migratedStorage.cartItems),
    clicks: attributionRemoveExpiredItems(migratedStorage.clicks),
  }

  if (
    migratedStorage !== parsedStorage ||
    storage.cartItems.length !== migratedStorage.cartItems.length ||
    storage.clicks.length !== migratedStorage.clicks.length
  ) {
    searchAttributionWrite(storage)
  }

  return storage
}
